// WorkbenchInterceptorPane — Module 6 centre-pane for the "Interceptor"
// section. Attach a guidance profile, run the 4-profile compare with
// miss-distance bar chart, and scrub a single-seed diagnostic harness
// on the Leaflet map with chevron markers for
// first_detection / first_lock_loss / first_reacquire.
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Play, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ErrorBar,
    ResponsiveContainer, ComposedChart, Line, Legend,
} from "recharts";
import { formatApiError } from "@/lib/formatApiError";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import SpinnerButton from "@/components/SpinnerButton";
import { sim } from "@/api/endpoints";

const MapEditor = lazy(() => import("@/components/terrain/MapEditor"));
const NONE = "__none__";

const MODE_LABELS = {
    pure_pursuit: "Pure Pursuit",
    pn: "PN",
    apn: "APN",
    clos: "CLOS",
};

function ModeChip({ mode }) {
    return (
        <span className="inline-flex items-center border border-ux-border-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-ux-text-3">
            {MODE_LABELS[mode] || mode}
        </span>
    );
}

export default function WorkbenchInterceptorPane({ scenario, readOnly = false, onScenarioChanged }) {
    const scenarioId = scenario?.id;
    const [library, setLibrary] = useState([]);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // Guidance compare state
    const [comparePending, setComparePending] = useState(false);
    const [compareSeeds, setCompareSeeds] = useState(32);
    const [compareIds, setCompareIds] = useState([]);
    const [compareResult, setCompareResult] = useState(null);
    // Module 7 — Pk vs miss-distance overlay (null until first run).
    const [pkResult, setPkResult] = useState(null);

    // Diagnostic harness state
    const [harnessData, setHarnessData] = useState(null);
    const [harnessLoading, setHarnessLoading] = useState(false);
    const [scrubIndex, setScrubIndex] = useState(0);

    // Load library once.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const list = await sim.guidanceProfiles.list({ scope: "tenant" });
                if (alive) setLibrary(Array.isArray(list) ? list : []);
            } catch (e) {
                toast.error(formatApiError(e, "Load failed"));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Hydrate attached profile from scenario.guidance_profile_id.
    useEffect(() => {
        let alive = true;
        if (!scenario?.guidance_profile_id) {
            setProfile(null);
            return () => {};
        }
        sim.guidanceProfiles
            .get(scenario.guidance_profile_id)
            .then((p) => { if (alive) setProfile(p); })
            .catch(() => { if (alive) setProfile(null); });
        return () => { alive = false; };
    }, [scenario?.guidance_profile_id]);

    // Default compare selection — the 4 preset profiles.
    useEffect(() => {
        if (compareIds.length > 0 || library.length === 0) return;
        const presets = library.filter((p) => p.is_preset).slice(0, 4);
        setCompareIds(presets.map((p) => p.id));
    }, [library, compareIds.length]);

    const onAttach = async (val) => {
        if (readOnly) return;
        try {
            if (val === NONE) {
                await sim.scenarios.detachGuidance(scenarioId);
                toast.success("Guidance detached");
            } else {
                await sim.scenarios.attachGuidance(scenarioId, val);
                toast.success("Guidance attached");
            }
            onScenarioChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Attach failed"));
        }
    };

    const runCompare = useCallback(async () => {
        if (!scenarioId || compareIds.length === 0) return;
        setComparePending(true);
        try {
            const res = await sim.scenarios.guidanceCompare(scenarioId, {
                profile_ids: compareIds,
                seeds: Math.max(1, Math.min(64, Number(compareSeeds) || 32)),
            });
            setCompareResult(res);
            // Module 7 — Pk overlay. When an engagement is attached,
            // call /pk-curve with the SAME seeds + profile_ids so the
            // overlay and the bar chart are deterministically aligned.
            if (scenario?.engagement_id) {
                try {
                    const pk = await sim.scenarios.pkCurve(scenarioId, {
                        engagement_id: scenario.engagement_id,
                        profile_ids: compareIds,
                        seeds: Math.max(1, Math.min(64, Number(compareSeeds) || 32)),
                    });
                    setPkResult(pk);
                } catch (_e) {
                    // Soft-fail — compare is still useful without Pk.
                    setPkResult(null);
                }
            } else {
                setPkResult(null);
            }
        } catch (e) {
            toast.error(formatApiError(e, "Compare failed"));
            setCompareResult(null);
            setPkResult(null);
        } finally {
            setComparePending(false);
        }
    }, [scenarioId, compareIds, compareSeeds, scenario?.engagement_id]);

    const runHarness = useCallback(async () => {
        if (!scenarioId || !profile) return;
        setHarnessLoading(true);
        try {
            const res = await sim.scenarios.diagnosticHarness(scenarioId, {
                seed: 0, horizon_s: 45, dt_s: 0.1,
                guidance_profile_id: profile.id,
            });
            setHarnessData(res);
            setScrubIndex(0);
        } catch (e) {
            toast.error(formatApiError(e, "Harness failed"));
            setHarnessData(null);
        } finally {
            setHarnessLoading(false);
        }
    }, [scenarioId, profile]);

    // Auto-run the harness once a profile is attached and a threat is present.
    useEffect(() => {
        if (profile && scenario?.threat_ids?.length > 0) {
            runHarness();
        }
    }, [profile, scenario?.threat_ids?.length, runHarness]);

    const compareChartData = useMemo(() => {
        if (!compareResult?.profiles) return [];
        const byId = new Map(
            (pkResult?.profiles || []).map((p) => [p.profile_id, p]),
        );
        return compareResult.profiles.map((p) => {
            const pk = byId.get(p.profile_id);
            return {
                name: (p.profile_name || "").split(" — ")[0] || p.mode,
                profile_id: p.profile_id,
                mean: Math.round((p.mean_m || 0) * 100) / 100,
                sigma: Math.round((p.sigma_m || 0) * 100) / 100,
                cep95: Math.round((p.cep_95_m || 0) * 100) / 100,
                // Pk plotted on a secondary 0–1 scale when engagement attached.
                pk: pk ? Math.round((pk.mean_pk || 0) * 1000) / 1000 : null,
            };
        });
    }, [compareResult, pkResult]);

    // Module 7 — "Best profile" summary derived from the Pk overlay.
    const bestProfile = useMemo(() => {
        if (!pkResult?.profiles?.length) return null;
        const best = [...pkResult.profiles].sort(
            (a, b) => (b.mean_pk - a.mean_pk) || (a.mean_miss_m - b.mean_miss_m),
        )[0];
        return best;
    }, [pkResult]);

    // Phase 14.8 W5 — wrap `scrubTicks` and `markers_markers` in useMemo
    // so their identities are stable across renders when the underlying
    // `harnessData` hasn't changed; otherwise the dependent useMemo
    // hooks at lines 222 / 244 / 265 recompute on every render.
    const scrubTicks = useMemo(() => harnessData?.ticks || [], [harnessData?.ticks]);
    const markerCount = scrubTicks.length;
    const currentTick = scrubTicks[Math.min(scrubIndex, Math.max(0, markerCount - 1))];
    const mapMarkers = useMemo(() => {
        if (!currentTick) return [];
        return [
            { lat: currentTick.int_lat, lon: currentTick.int_lon, color: "#155e75", id: "int", radius: 8 },
            { lat: currentTick.tgt_lat, lon: currentTick.tgt_lon, color: "#b45309", id: "tgt", radius: 8 },
        ];
    }, [currentTick]);

    const mapPolylines = useMemo(() => {
        if (scrubTicks.length < 2) return [];
        const int_ring = scrubTicks.map((t) => [t.int_lat, t.int_lon]);
        const tgt_ring = scrubTicks.map((t) => [t.tgt_lat, t.tgt_lon]);
        return [
            { ring: int_ring, id: "int-trail", color: "#0f766e" },
            { ring: tgt_ring, id: "tgt-trail", color: "#c2410c" },
        ];
    }, [scrubTicks]);

    const markers_markers = useMemo(() => harnessData?.markers || {}, [harnessData?.markers]);

    // Chevron positions on the scrubber — event tick indices.
    const chevronPositions = useMemo(() => {
        if (markerCount === 0) return [];
        const eventTimeToIdx = (t) => {
            if (t == null) return null;
            const idx = scrubTicks.findIndex((x) => x.t_s >= t);
            return idx < 0 ? null : idx;
        };
        return [
            { kind: "detection", label: "det", color: "#15803d",
              idx: eventTimeToIdx(markers_markers.first_detection) },
            { kind: "lock_loss", label: "lost", color: "#b91c1c",
              idx: eventTimeToIdx(markers_markers.first_lock_lost) },
            { kind: "reacquired", label: "reacq", color: "#0ea5e9",
              idx: eventTimeToIdx(markers_markers.first_reacquired) },
            { kind: "c2_loss", label: "c2-loss", color: "#a855f7",
              idx: eventTimeToIdx(markers_markers.c2_loss_at) },
        ].filter((c) => c.idx != null);
    }, [markers_markers, scrubTicks, markerCount]);

    const currentStatus = currentTick?.tracked ? "tracking" : "searching";
    const currentSNR = currentTick ? `${currentTick.snr_db.toFixed(1)} dB` : "—";
    const currentLOS = currentTick ? `${currentTick.los_m.toFixed(1)} m` : "—";

    const toggleCompare = (id) => {
        setCompareIds((ids) =>
            ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id].slice(-4),
        );
    };

    const mapCentre = useMemo(() => {
        if (currentTick) {
            return [
                (currentTick.int_lat + currentTick.tgt_lat) / 2,
                (currentTick.int_lon + currentTick.tgt_lon) / 2,
            ];
        }
        if (scrubTicks[0]) return [scrubTicks[0].int_lat, scrubTicks[0].int_lon];
        return [51.51, 0.0];
    }, [currentTick, scrubTicks]);

    if (loading) {
        return <div className="text-xs text-ux-text-3">Loading guidance profiles…</div>;
    }

    return (
        <div className="space-y-4" data-testid="workbench-interceptor-pane">
            {/* Attach + summary row */}
            <div
                className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 flex flex-wrap items-end gap-4"
                data-testid="workbench-interceptor-attach"
            >
                <label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3 space-y-1">
                    Guidance profile
                    <Select
                        value={profile?.id || NONE}
                        onValueChange={onAttach}
                        disabled={readOnly}
                    >
                        <SelectTrigger
                            className="w-[300px] bg-ux-bg-2 text-[12px] font-mono"
                            data-testid="workbench-interceptor-select"
                        >
                            <SelectValue placeholder="— none —" />
                        </SelectTrigger>
                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                            <SelectItem value={NONE} className="text-[12px] font-mono">
                                — none —
                            </SelectItem>
                            {library.map((p) => (
                                <SelectItem
                                    key={p.id}
                                    value={p.id}
                                    className="text-[12px] font-mono"
                                    data-testid={`workbench-interceptor-option-${p.id}`}
                                >
                                    {p.name} · {MODE_LABELS[p.mode] || p.mode}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </label>
                {profile ? (
                    <div className="text-[11px] font-mono text-ux-text-3" data-testid="workbench-interceptor-summary">
                        Mode: <strong className="text-ux-text-1">{MODE_LABELS[profile.mode]}</strong>{" "}
                        · Seeker: <strong className="text-ux-text-1">{(profile.seeker_type || "").toUpperCase()}</strong>{" "}
                        · Lock-loss in {" "}
                        <strong className="text-ux-text-1">
                            {profile.lock_loss?.consecutive_ticks_below_snr_to_lose ?? "—"}
                        </strong>{" "}
                        ticks
                    </div>
                ) : null}
                <Link
                    to="/plan/guidance-profiles"
                    className="ml-auto text-xs text-ux-accent underline decoration-dotted"
                >
                    Open guidance library <ExternalLink className="inline w-3 h-3" />
                </Link>
            </div>

            {!profile ? (
                <EmptyState
                    title="No guidance profile attached"
                    body="Pick a profile above — engagement runs will use pure pursuit as a fallback otherwise."
                />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Guidance compare panel */}
                    <div
                        className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 space-y-3"
                        data-testid="workbench-guidance-compare"
                    >
                        <div className="flex items-center gap-3">
                            <h4 className="font-mono text-xs uppercase tracking-wider text-ux-text-3 flex-1">
                                Guidance compare
                            </h4>
                            <label className="text-[10px] font-mono uppercase text-ux-text-3">
                                seeds
                                <Input
                                    type="number"
                                    min={1}
                                    max={64}
                                    value={compareSeeds}
                                    onChange={(e) => setCompareSeeds(e.target.value)}
                                    className="h-7 w-16 text-[12px] font-mono ml-1 inline-block"
                                    data-testid="workbench-compare-seeds"
                                />
                            </label>
                            <SpinnerButton
                                size="sm"
                                loading={comparePending}
                                onClick={runCompare}
                                testId="workbench-compare-run"
                            >
                                <Play className="h-3.5 w-3.5 mr-1" />
                                Compare
                            </SpinnerButton>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {library.filter((p) => p.is_preset).map((p) => {
                                const on = compareIds.includes(p.id);
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => toggleCompare(p.id)}
                                        className={`text-[10px] font-mono rounded-full border px-2 py-0.5 ${
                                            on
                                                ? "border-ux-accent/60 bg-ux-accent/10 text-ux-accent"
                                                : "border-ux-border-1 text-ux-text-3 hover:border-ux-border-2"
                                        }`}
                                        data-testid={`workbench-compare-toggle-${p.id}`}
                                    >
                                        {(p.name || p.mode).split(" — ")[0]}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="h-[220px]" data-testid="workbench-compare-chart">
                            {compareResult ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={compareChartData}>
                                        <CartesianGrid stroke="rgba(0,0,0,0.08)" />
                                        <XAxis dataKey="name" tick={{ fontFamily: "monospace", fontSize: 10 }} />
                                        <YAxis yAxisId="miss" unit=" m" tick={{ fontFamily: "monospace", fontSize: 10 }} />
                                        {pkResult ? (
                                            <YAxis
                                                yAxisId="pk"
                                                orientation="right"
                                                domain={[0, 1]}
                                                tick={{ fontFamily: "monospace", fontSize: 10 }}
                                            />
                                        ) : null}
                                        <Tooltip wrapperStyle={{ fontFamily: "monospace", fontSize: 11 }} />
                                        {pkResult ? (
                                            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }} />
                                        ) : null}
                                        <Bar yAxisId="miss" dataKey="mean" fill="#0f766e" name="Mean miss (m)">
                                            <ErrorBar dataKey="sigma" width={4} stroke="#15803d" />
                                        </Bar>
                                        {pkResult ? (
                                            <Line
                                                yAxisId="pk"
                                                type="monotone"
                                                dataKey="pk"
                                                name="p(kill) @ mean miss"
                                                stroke="#1e3a8a"
                                                strokeWidth={2}
                                                dot={{ r: 4, fill: "#1e3a8a" }}
                                                isAnimationActive={false}
                                                data-testid="workbench-compare-pk-overlay"
                                            />
                                        ) : null}
                                    </ComposedChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-[11px] font-mono text-ux-text-3">
                                    Pick 2–4 profiles and click <em>Compare</em>.
                                </div>
                            )}
                        </div>
                        {bestProfile ? (
                            <div
                                className="border border-ux-accent/30 bg-ux-accent/5 rounded-md px-3 py-2 text-[11px] font-mono"
                                data-testid="workbench-best-profile-card"
                            >
                                <div className="uppercase tracking-wider text-ux-accent text-[10px] mb-1">
                                    Best profile
                                </div>
                                <div className="text-ux-text-1">
                                    {(bestProfile.profile_name || "").split(" — ")[0] || bestProfile.mode}
                                    {" — "}
                                    <strong>{bestProfile.mean_pk.toFixed(2)}</strong> p(kill) @ mean miss{" "}
                                    <strong>{bestProfile.mean_miss_m.toFixed(1)} m</strong>
                                </div>
                            </div>
                        ) : null}
                        {compareResult ? (
                            <table className="w-full text-[11px] font-mono" data-testid="workbench-compare-table">
                                <thead className="text-ux-text-3">
                                    <tr>
                                        <th className="text-left">Profile</th>
                                        <th className="text-right">Mean</th>
                                        <th className="text-right">σ</th>
                                        <th className="text-right">CEP95</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {compareResult.profiles.map((p) => (
                                        <tr key={p.profile_id} className="border-t border-ux-border-1/60">
                                            <td>{(p.profile_name || "").split(" — ")[0]}</td>
                                            <td className="text-right">{p.mean_m.toFixed(2)} m</td>
                                            <td className="text-right">{p.sigma_m.toFixed(2)}</td>
                                            <td className="text-right">{p.cep_95_m.toFixed(2)} m</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : null}
                    </div>

                    {/* Diagnostic harness + map + scrubber */}
                    <div
                        className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 space-y-3"
                        data-testid="workbench-diagnostic-harness"
                    >
                        <div className="flex items-center gap-3">
                            <h4 className="font-mono text-xs uppercase tracking-wider text-ux-text-3 flex-1">
                                Diagnostic harness
                            </h4>
                            <span
                                className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                                    currentStatus === "tracking"
                                        ? "border-ux-success/40 bg-ux-success/10 text-ux-success"
                                        : "border-ux-warning/40 bg-ux-warning/10 text-ux-warning"
                                }`}
                                data-testid="workbench-harness-status"
                            >
                                {currentStatus}
                            </span>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={runHarness}
                                disabled={harnessLoading}
                                data-testid="workbench-harness-refresh"
                            >
                                <RefreshCcw className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                        <Suspense
                            fallback={
                                <div className="h-[260px] rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-500">
                                    Loading map…
                                </div>
                            }
                        >
                            <MapEditor
                                centre={mapCentre}
                                zoom={15}
                                polylines={mapPolylines}
                                markers={mapMarkers}
                                heightPx={260}
                                testId="workbench-harness-map"
                            />
                        </Suspense>
                        {markerCount > 0 ? (
                            <div className="space-y-2" data-testid="workbench-harness-scrubber">
                                <div className="relative h-3">
                                    {chevronPositions.map((c) => {
                                        const left = (c.idx / Math.max(1, markerCount - 1)) * 100;
                                        return (
                                            <div
                                                key={c.kind}
                                                title={`${c.label} @ t=${(scrubTicks[c.idx]?.t_s || 0).toFixed(1)}s`}
                                                style={{ left: `${left}%`, backgroundColor: c.color }}
                                                className="absolute top-0 w-1.5 h-3 rounded-sm -translate-x-1/2"
                                                data-testid={`workbench-harness-chevron-${c.kind}`}
                                            />
                                        );
                                    })}
                                </div>
                                <input
                                    type="range"
                                    min={0}
                                    max={markerCount - 1}
                                    value={scrubIndex}
                                    onChange={(e) => setScrubIndex(Number(e.target.value))}
                                    className="w-full accent-ux-accent"
                                    data-testid="workbench-harness-slider"
                                />
                                <div className="flex justify-between text-[10px] font-mono text-ux-text-3">
                                    <span>t = {(currentTick?.t_s ?? 0).toFixed(2)} s</span>
                                    <span>SNR {currentSNR}</span>
                                    <span>LOS {currentLOS}</span>
                                    <span>miss {((harnessData?.miss_distance_m ?? 0) || 0).toFixed(2)} m</span>
                                </div>
                                <div className="flex flex-wrap gap-2 text-[10px] font-mono">
                                    <span className="inline-flex items-center gap-1">
                                        <span className="w-2 h-2 bg-ux-success inline-block rounded-sm" />
                                        first detection
                                        {" "}
                                        {markers_markers.first_detection != null
                                            ? `t=${markers_markers.first_detection.toFixed(1)}s`
                                            : "—"}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <span className="w-2 h-2 bg-ux-danger inline-block rounded-sm" />
                                        lock loss
                                        {" "}
                                        {markers_markers.first_lock_lost != null
                                            ? `t=${markers_markers.first_lock_lost.toFixed(1)}s`
                                            : "—"}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <span className="w-2 h-2 bg-sky-500 inline-block rounded-sm" />
                                        reacquired
                                        {" "}
                                        {markers_markers.first_reacquired != null
                                            ? `t=${markers_markers.first_reacquired.toFixed(1)}s`
                                            : "—"}
                                    </span>
                                    <span className="inline-flex items-center gap-1" data-testid="workbench-marker-c2-loss">
                                        <span className="w-2 h-2 bg-purple-500 inline-block rounded-sm" />
                                        c2 loss
                                        {" "}
                                        {markers_markers.c2_loss_at != null
                                            ? `t=${markers_markers.c2_loss_at.toFixed(1)}s`
                                            : "—"}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="text-[11px] font-mono text-ux-text-3">
                                {harnessLoading ? "Computing harness…" : "Attach a threat to a scenario to run the harness."}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
